import React, {MouseEvent, ReactElement, useCallback, useEffect, useRef, useState} from "react";
import {Sync} from "@mui/icons-material";

import {
    ACard,
    ADataGrid,
    ADataGridFilter,
    PagedSearchParams,
    Period,
} from "@atiautomacao/ati-ui-library";
import {
    Box,
    CircularProgress,
    Grid,
    Link,
    LinearProgress,
    linearProgressClasses,
    styled,
    Typography,
    useTheme,
} from "@mui/material";
import {extractFiltersFromColumns} from "../../../Utils/DataUitils";
import {RootState} from "../../../Config/Store";
import {useAppDispatch, useAppSelector} from "../../../Config/Hooks";
import {clear, Performance, searchAnalisysData, searchPowerStations} from "./PerformanceAnalysisReducer";
import {usePowerStationNavigation} from '../../../Shared/Hooks/usePowerStationNavigation';
import {getIconColor} from "../../../Shared/Components/Layout/menu/helpers/getIconColor";
import {MenuItemStatus} from "../../../Shared/Components/Layout/menu/types/MenuItemStatus";
import {performanceAnalysisColumns} from "./PerformanceAnalysisConfig";
import useInterval from "../../../Shared/Hooks/useInterval";
import ActivePowerChart from "./ActivePowerChart";
import {getStatusName} from "../../../Shared/Components/Layout/menu/helpers/getStatusName";

const getTodayPeriod = (): { fromDateTime: Date; toDateTime: Date } => {
    const toDateTime = new Date();
    const fromDateTime = new Date();
    fromDateTime.setHours(0, 0, 0, 0);
    return {fromDateTime, toDateTime};
};

const PerformanceAnalysisPage = () : ReactElement => {
    const theme = useTheme();
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);

    const initialPeriod: Period = {
        groupBy: "week",
        fromDateTime: new Date(new Date().setHours(0, 0, 0, 0)),
        toDateTime: new Date()
    };

    const [period, setPeriod] = useState<Period>(initialPeriod);

    const dispatch = useAppDispatch();
    const { entities, loading, powerStations } = useAppSelector((state: RootState) => state.performanceAnalysis);
    const totalOfRecords = useAppSelector((state : RootState) => state.performanceAnalysis.totalOfRecords);
    const navigationToPowerStation = usePowerStationNavigation();
    const entityMap =  useRef(new Map<number, Performance>()).current;

    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);
    const [rows, setRows] = useState<Array<any>>([]);
    const [pagedSearchParams, setPagedSearchParams] = useState(new PagedSearchParams(extractFiltersFromColumns(performanceAnalysisColumns), 0, userConfig.sizePagePerformanceAnalysis));

    const handleOnFiltersChange = useCallback((dataGridFilters: Array<ADataGridFilter>, page: number, pageSize: number) => {
        let newPageParams: PagedSearchParams = new PagedSearchParams(dataGridFilters, page, pageSize);
        setPagedSearchParams(
            newPageParams
        );
    },[]);

    const [fromDateTime, setFromDateTime] = useState<Date>();
    const [toDateTime, setToDateTime] = useState<Date>();
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [isFirstLoad, setIsFirstLoad] = useState(true);

    const statusColor = (status: MenuItemStatus | undefined) => {
        if (!status) {
            return <CircularProgress size={20} />
        }
        const color =  getIconColor(status);
        const sync = status.includes("Sincronizando");
        return <div style={{
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            margin: "10px",
            float: "left",
            background: sync ? 'transparent' : color
        }} title={getStatusName(status)}>{sync ? <Sync style={{color: color}}  /> : ''}</div>
    }

    const verifyProgress = (value: number) => {
        if (value >= 70 && value <= 95) {
            return "#a4f657"
        }else if (value >= 60 || value > 95) {
            return "#FFD700";
        }else if (value < 60 ) {
            return "#FF4500"
        }
    };

    const handleNavigation = (event: MouseEvent<HTMLElement>, powerPlantId: number): void => {
        event.preventDefault();
        navigationToPowerStation(powerPlantId);
    }

    const BorderLinearProgress = styled(LinearProgress)(({ theme, value }) => ({
        height: 20,
        borderRadius: 5,
        [`&.${linearProgressClasses.colorPrimary}`]: {
            backgroundColor: theme.palette.grey,
        },
        [`& .${linearProgressClasses.bar}`]: {
            borderRadius: 5,
            backgroundColor: verifyProgress(value ?? 0),
        },
    }));

    const isLightMode = () => {
        return theme.palette.mode === 'light'
    }

    // @ts-ignore
    function LinearProgressWithLabel({value}) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: '100%', mr: 1 }}>
                    <BorderLinearProgress variant="determinate" value={value}/>
                </Box>
                <Box sx={{ position: 'relative', minWidth: 35, right: '50%'}}>
                    <Typography variant="body2">{`${Math.round(value)}%`}</Typography>
                </Box>
            </Box>
        );
    }

    function irradianceSum (element: number) : number | string {
        return element ? parseFloat(element.toString()).toFixed(2) : " ";
    }

    const GeneratedInjected = (generated:number, injected:number) => {
        return(
            <Box  >
                <Typography variant="subtitle1" fontSize={12} color={isLightMode() ? "#767676" : "#fff"}>Gerada: <label style={{color: isLightMode() ? "#000" : "#fff"}}>{generated ? `${generated.toFixed(2) + " MWh "}` : " "} </label></Typography>
                <Typography variant="subtitle1" fontSize={12} color={isLightMode() ? "#767676" : "#fff"}>Injetada: <label style={{color: isLightMode() ? "#000" : "#fff"}}>{injected ? `${injected.toFixed(2) + " MWh "}` : " "} </label></Typography>
            </Box>
        )
    }

    const structureRow = (entitiesValues: Array<Performance>) => {
        let entityAlarms: Array<any> = []
        entitiesValues.forEach( element => {
            const entity = entityMap.get(element.powerStationId);
            entityAlarms.push({
                status: statusColor(entity?.status),
                powerStationName: <Link href="#" onClick={(event) => handleNavigation(event, element.powerStationId)} underline="hover">{element.powerStationName}</Link>,
                powerCapacityNominal: element.powerCapacityNominal ? parseInt(element.powerCapacityNominal.toString()) : " ",
                instantPower: entity && entity.instantPowerList ? <div style={{width: '90%', display: 'flex', flexDirection: 'row', textAlign: 'center', alignItems: 'center'}}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <ActivePowerChart dataInstantPower={entity.instantPowerList} powerStation={element.powerStationName}/>
                    </Box>
                    <Box>{element.instantPower ? parseFloat(element.instantPower.toString()).toFixed(2) : " "}</Box>

                </div>
                : <div style={{width: '90%', display: 'flex', flexDirection: 'row', textAlign: 'center', alignItems: 'center'}}>
                    <Box>{element.instantPower ? parseFloat(element.instantPower.toString()).toFixed(2) : " "}</Box>
                </div>,
                irradianceSum: <Box>{irradianceSum(element.irradianceSum)}</Box>,

                generatedEnergySum: (
                    <Box>
                        {GeneratedInjected(
                            element.generatedEnergySum,
                            element.injectedEnergySum
                        )}
                    </Box>
                ),
                performanceRatio: <LinearProgressWithLabel value={element.performanceRatio || 0} /> ,
                availability: element.availability ? parseInt(element.availability.toString()) + " %" : " ",
            });
        });
        setRows(entityAlarms);
    }

    useEffect(() => {
        if(fromDateTime !== undefined && toDateTime !== undefined){
            let periodWeek: Period = {groupBy: "week", fromDateTime, toDateTime};
            dispatch(searchPowerStations({period: periodWeek, actualDay: period, params: pagedSearchParams}));
        }
    }, [pagedSearchParams])

    useInterval(() => {
        const {fromDateTime: todayStart, toDateTime: todayNow} = getTodayPeriod();
        setFromDateTime(todayStart);
        setToDateTime(todayNow);
        let periodWeek: Period = {groupBy: "week", fromDateTime: todayStart, toDateTime: todayNow};
        dispatch(searchPowerStations({period: periodWeek, actualDay: period, params: pagedSearchParams}));
    }, userConfig.mainUpdateInterval) // 5 minutes

    useEffect(() => {
        setIsLoadingData(false);
        if(entities && entities.length > 0){
            entityMap.clear();
            // create a map grouping entities by powerStationId
            entities.forEach(entity => {
                entityMap.set(entity.powerStationId, entity);
            });
            if(powerStations && powerStations.length > 0){
                structureRow(powerStations);
            }
        }
    }, [entities])

    useEffect(() => {
        if(powerStations && powerStations.length > 0){
            structureRow(powerStations);
            if(fromDateTime !== undefined && toDateTime !== undefined) {
                let periodWeek: Period = {groupBy: "week", fromDateTime, toDateTime};
                setIsLoadingData(true);
                dispatch(searchAnalisysData({powerStationIds: powerStations.map(ps => ps.powerStationId), period: periodWeek, actualDay: period}))
            }
        }
        else{
            structureRow([]);
        }
        if (fromDateTime !== undefined && toDateTime !== undefined) {
            setIsFirstLoad(false);
        }
    }, [powerStations, theme])

    useEffect(() => {
        dispatch(clear());
        setRows([]);
        const {fromDateTime: todayStart, toDateTime: todayNow} = getTodayPeriod();
        setFromDateTime(todayStart);
        setToDateTime(todayNow);
        let periodWeek: Period = {groupBy: "week", fromDateTime: todayStart, toDateTime: todayNow};
        dispatch(searchPowerStations({period: periodWeek, actualDay: period, params: pagedSearchParams}));
    }, []);

    return (
        <Grid container>
            <Grid item xs={12}>
                <ACard
                    key={`performance-analysis-${openSubMenu}`}
                    styleProps={{
                        headerStyle: {
                            display: 'none',
                            backgroundColor: 'rgb(247, 247, 247)'
                        },
                        footerStyle: { padding: 18, paddingTop: 0 },
                        contentStyle: { padding: '0 16px 10px 0' },
                    }}
                >
                    <ADataGrid
                        hideSelection={true}
                        ariaLabel={'overview-performance-analysis'}
                        hideFilters={true}
                        size={"small"}
                        columns={performanceAnalysisColumns}
                        rows={rows}
                        headerStyle={{ color: '#fff'}}
                        page={pagedSearchParams.page}
                        loading={loading && !isLoadingData && isFirstLoad}
                        totalOfRecords={totalOfRecords || 0}
                        rowsPerPage={pagedSearchParams.size}
                        onFiltersChange={handleOnFiltersChange}
                        showSortOptions={true}
                    />
                </ACard>
            </Grid>

        </Grid>

    )

}

export default PerformanceAnalysisPage;
